import { Link } from "react-router-dom";
import { Phone, MessageSquare, Instagram, Facebook, ChevronRight, Calendar, Zap, Shield, Clock, Star, ArrowLeft } from "lucide-react";
import { useEffect } from "react";

const PHONE_NUMBER = "5145545916";
const SOCIAL_LINKS = {
  messenger: "https://m.me/esthetiquesignemelanie",
  instagram: "https://www.instagram.com/esthetiquesignemg"
};

// SEO Page Layout
const SEOPageLayout = ({ children, title }) => {
  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Navigation */}
      <nav className="bg-white shadow-sm py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#5C4B3F] hover:text-[#8B7355]">
            <ArrowLeft size={20} />
            <span className="text-sm">Retour à l'accueil</span>
          </Link>
          <a 
            href="/#contact" 
            className="px-5 py-2 bg-[#5C4B3F] text-white text-xs uppercase tracking-wider hover:bg-[#8B7355] transition-colors"
          >
            Réserver
          </a>
        </div>
      </nav>
      
      {children}
      
      {/* Footer */}
      <footer className="py-8 bg-[#5C4B3F] text-white/60 text-center text-sm">
        <div className="max-w-4xl mx-auto px-4">
          <p className="mb-2">© {new Date().getFullYear()} Esthétique Signé Mélanie Gauthier</p>
          <Link to="/" className="text-white/80 hover:text-white">Retour à l'accueil</Link>
        </div>
      </footer>
    </div>
  );
};

// Épilation Laser Page
export const EpilationLaserPage = () => (
  <SEOPageLayout title="Épilation Laser Saint-Hilaire | Soprano ICE Platinum | Sans Douleur">
    <article className="py-16">
      <div className="max-w-4xl mx-auto px-4">
        {/* Hero */}
        <header className="text-center mb-12">
          <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-2">Épilation permanente</p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#5C4B3F] mb-4">
            Épilation Laser Soprano ICE Platinum à Saint-Hilaire
          </h1>
          <p className="text-[#5C4B3F]/70 text-lg max-w-2xl mx-auto">
            La technologie la plus avancée pour une épilation définitive, rapide et pratiquement indolore. Idéal pour tous les types de peau.
          </p>
        </header>

        {/* Benefits */}
        <section className="grid md:grid-cols-3 gap-6 mb-12" aria-label="Avantages du laser">
          <div className="bg-white p-6 text-center">
            <Zap className="mx-auto mb-4 text-[#5C4B3F]" size={32} aria-hidden="true" />
            <h2 className="font-medium text-[#5C4B3F] mb-2">Technologie 3-en-1</h2>
            <p className="text-[#5C4B3F]/70 text-sm">Trois longueurs d'onde pour cibler tous les types de poils</p>
          </div>
          <div className="bg-white p-6 text-center">
            <Shield className="mx-auto mb-4 text-[#5C4B3F]" size={32} aria-hidden="true" />
            <h2 className="font-medium text-[#5C4B3F] mb-2">Sans douleur</h2>
            <p className="text-[#5C4B3F]/70 text-sm">Système de refroidissement intégré pour votre confort</p>
          </div>
          <div className="bg-white p-6 text-center">
            <Clock className="mx-auto mb-4 text-[#5C4B3F]" size={32} aria-hidden="true" />
            <h2 className="font-medium text-[#5C4B3F] mb-2">Traitement rapide</h2>
            <p className="text-[#5C4B3F]/70 text-sm">Séances courtes grâce à la méthode In-Motion</p>
          </div>
        </section>

        {/* Content */}
        <section className="bg-white p-8 mb-12">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#5C4B3F] mb-4">
            Pourquoi choisir le laser Soprano ICE Platinum?
          </h2>
          <div className="space-y-4 text-[#5C4B3F]/80">
            <p>
              Le laser <strong>Soprano ICE Platinum</strong> représente l'évolution ultime de la technologie d'épilation laser. 
              Contrairement aux lasers traditionnels, il combine trois longueurs d'onde (Alexandrite 755nm, 
              Diode 810nm et Nd:YAG 1064nm) dans un seul applicateur.
            </p>
            <p>
              Cette technologie unique permet de traiter efficacement <strong>tous les types de peau</strong>, 
              des plus claires aux plus foncées, ainsi que <strong>tous les types de poils</strong>, 
              qu'ils soient fins ou épais.
            </p>
            <p>
              Le système de refroidissement ICE garantit un traitement pratiquement <strong>indolore</strong>, 
              même sur les zones les plus sensibles comme le maillot ou les aisselles.
            </p>
          </div>
        </section>

        {/* Pricing Preview */}
        <section className="bg-[#5C4B3F] text-white p-8 mb-12" aria-label="Tarifs épilation laser">
          <h2 className="font-['Playfair_Display'] text-2xl mb-4 text-center">Tarifs épilation laser à Saint-Hilaire</h2>
          <div className="grid sm:grid-cols-2 gap-4 text-sm mb-6">
            <div className="flex justify-between py-2 border-b border-white/20">
              <span>Aisselles</span><span>70$</span>
            </div>
            <div className="flex justify-between py-2 border-b border-white/20">
              <span>Bikini régulier</span><span>80$</span>
            </div>
            <div className="flex justify-between py-2 border-b border-white/20">
              <span>Demi-jambes</span><span>160$</span>
            </div>
            <div className="flex justify-between py-2 border-b border-white/20">
              <span>Jambes complètes</span><span>300$</span>
            </div>
          </div>
          <div className="text-center">
            <p className="text-white/70 mb-4">Forfait 3 régions = 20% de rabais</p>
            <Link 
              to="/#tarifs" 
              className="inline-block px-6 py-3 bg-white text-[#5C4B3F] text-sm uppercase tracking-wider hover:bg-[#F9F8F6]"
            >
              Voir tous les tarifs
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#5C4B3F] mb-6 text-center">
            Questions fréquentes sur l'épilation laser
          </h2>
          <div className="space-y-4">
            <details className="bg-white p-4">
              <summary className="font-medium text-[#5C4B3F] cursor-pointer">Combien de séances sont nécessaires?</summary>
              <p className="mt-2 text-[#5C4B3F]/70">En général, 6 à 8 séances espacées de 4 à 6 semaines sont recommandées pour des résultats optimaux.</p>
            </details>
            <details className="bg-white p-4">
              <summary className="font-medium text-[#5C4B3F] cursor-pointer">L'épilation laser est-elle douloureuse?</summary>
              <p className="mt-2 text-[#5C4B3F]/70">Avec le Soprano ICE Platinum, le traitement est pratiquement indolore grâce au système de refroidissement intégré.</p>
            </details>
            <details className="bg-white p-4">
              <summary className="font-medium text-[#5C4B3F] cursor-pointer">Le laser convient-il à tous les types de peau?</summary>
              <p className="mt-2 text-[#5C4B3F]/70">Oui, le Soprano ICE Platinum est efficace sur tous les types de peau, y compris les peaux bronzées et foncées.</p>
            </details>
          </div>
        </section>

        {/* CTA */}
        <footer className="text-center">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#5C4B3F] mb-4">
            Prête à dire adieu aux poils?
          </h2>
          <p className="text-[#5C4B3F]/70 mb-6">Consultation gratuite pour évaluer vos besoins</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${PHONE_NUMBER}`} className="inline-flex items-center gap-2 px-6 py-3 bg-[#5C4B3F] text-white text-sm uppercase tracking-wider hover:bg-[#8B7355]">
              <Phone size={18} aria-hidden="true" /> Appeler
            </a>
            <a href={SOCIAL_LINKS.messenger} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#5C4B3F] text-[#5C4B3F] text-sm uppercase tracking-wider hover:bg-[#5C4B3F] hover:text-white">
              <Facebook size={18} aria-hidden="true" /> Messenger
            </a>
          </div>
        </footer>
      </div>
    </article>
  </SEOPageLayout>
);

// Soins du Visage Page
export const SoinsVisagePage = () => (
  <SEOPageLayout title="Soins du Visage Saint-Hilaire | Institut Esthéderm | Esthéticienne">
    <article className="py-16">
      <div className="max-w-4xl mx-auto px-4">
        <header className="text-center mb-12">
          <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-2">Soins professionnels</p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#5C4B3F] mb-4">
            Soins du Visage Institut Esthéderm à Saint-Hilaire
          </h1>
          <p className="text-[#5C4B3F]/70 text-lg max-w-2xl mx-auto">
            Des protocoles personnalisés pour révéler l'éclat naturel de votre peau avec les produits Institut Esthéderm.
          </p>
        </header>

        <section className="bg-white p-8 mb-12">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#5C4B3F] mb-4">
            La science au service de votre peau
          </h2>
          <div className="space-y-4 text-[#5C4B3F]/80">
            <p>
              <strong>Institut Esthéderm</strong> est une marque française de soins dermocosmétiques, 
              reconnue mondialement pour son approche scientifique unique: l'écobiologie cellulaire.
            </p>
            <p>
              Chaque soin est formulé pour <strong>réactiver les mécanismes naturels</strong> de votre peau 
              et rétablir son équilibre. Les produits sont enrichis en Eau Cellulaire, 
              reproduisant l'environnement idéal pour vos cellules.
            </p>
            <p>
              Avec 28 ans d'expérience, Mélanie crée des protocoles sur mesure adaptés à vos besoins 
              spécifiques: <strong>anti-âge, hydratation, éclat</strong>, ou traitement de problèmes cutanés.
            </p>
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="bg-[#F9F8F6] p-6">
            <h3 className="font-medium text-[#5C4B3F] mb-2">Soins Anti-Âge</h3>
            <p className="text-[#5C4B3F]/70 text-sm">Lutter contre les signes du vieillissement et retrouver une peau ferme et lumineuse.</p>
          </div>
          <div className="bg-[#F9F8F6] p-6">
            <h3 className="font-medium text-[#5C4B3F] mb-2">Soins Hydratation</h3>
            <p className="text-[#5C4B3F]/70 text-sm">Restaurer l'hydratation en profondeur pour une peau souple et confortable.</p>
          </div>
          <div className="bg-[#F9F8F6] p-6">
            <h3 className="font-medium text-[#5C4B3F] mb-2">Soins Éclat</h3>
            <p className="text-[#5C4B3F]/70 text-sm">Révéler la luminosité naturelle de votre peau et unifier le teint.</p>
          </div>
          <div className="bg-[#F9F8F6] p-6">
            <h3 className="font-medium text-[#5C4B3F] mb-2">Soins Purifiants</h3>
            <p className="text-[#5C4B3F]/70 text-sm">Traiter les imperfections et réguler l'excès de sébum.</p>
          </div>
        </section>

        <footer className="text-center">
          <a href="/#contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#5C4B3F] text-white text-sm uppercase tracking-wider hover:bg-[#8B7355]">
            <Calendar size={18} aria-hidden="true" /> Réserver un soin
          </a>
        </footer>
      </div>
    </article>
  </SEOPageLayout>
);

// Extensions de Cils Page
export const ExtensionsCilsPage = () => (
  <SEOPageLayout title="Extensions de Cils Saint-Hilaire | Pose Professionnelle | Regard Sublimé">
    <article className="py-16">
      <div className="max-w-4xl mx-auto px-4">
        <header className="text-center mb-12">
          <p className="text-[#8B7355] text-sm tracking-widest uppercase mb-2">Regard sublimé</p>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#5C4B3F] mb-4">
            Extensions de Cils à Saint-Hilaire
          </h1>
          <p className="text-[#5C4B3F]/70 text-lg max-w-2xl mx-auto">
            Un regard naturellement magnifique, sans mascara. Pose professionnelle et personnalisée.
          </p>
        </header>

        <section className="bg-white p-8 mb-12">
          <h2 className="font-['Playfair_Display'] text-2xl text-[#5C4B3F] mb-4">
            Des extensions adaptées à vous
          </h2>
          <div className="space-y-4 text-[#5C4B3F]/80">
            <p>
              Les extensions de cils sont posées une à une sur vos cils naturels pour un résultat 
              parfaitement naturel. Plusieurs styles sont disponibles selon l'effet recherché: 
              <strong>naturel, glamour, ou dramatique</strong>.
            </p>
            <p>
              La durée de vie des extensions est de 3 à 4 semaines, avec des retouches recommandées 
              toutes les 2-3 semaines pour maintenir un résultat optimal.
            </p>
            <p>
              Chez Esthétique Signé Mélanie Gauthier, nous utilisons des produits de qualité professionnelle
              pour garantir le confort et la santé de vos cils naturels.
            </p>
          </div>
        </section>

        <section className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-[#F9F8F6] p-6 text-center">
            <h3 className="font-medium text-[#5C4B3F] mb-2">Classique</h3>
            <p className="text-[#5C4B3F]/70 text-sm">1 extension par cil naturel pour un effet naturel</p>
            <p className="text-[#8B7355] font-medium mt-2">À partir de 120$</p>
          </div>
          <div className="bg-[#F9F8F6] p-6 text-center">
            <h3 className="font-medium text-[#5C4B3F] mb-2">Volume Russe</h3>
            <p className="text-[#5C4B3F]/70 text-sm">Bouquets de 2-6 extensions pour plus de volume</p>
            <p className="text-[#8B7355] font-medium mt-2">À partir de 150$</p>
          </div>
          <div className="bg-[#F9F8F6] p-6 text-center">
            <h3 className="font-medium text-[#5C4B3F] mb-2">Retouches</h3>
            <p className="text-[#5C4B3F]/70 text-sm">Maintien toutes les 2-3 semaines</p>
            <p className="text-[#8B7355] font-medium mt-2">À partir de 60$</p>
          </div>
        </section>

        <footer className="text-center">
          <a href="/#contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#5C4B3F] text-white text-sm uppercase tracking-wider hover:bg-[#8B7355]">
            <Calendar size={18} aria-hidden="true" /> Prendre rendez-vous
          </a>
        </footer>
      </div>
    </article>
  </SEOPageLayout>
);
